/* eslint-disable import/no-extraneous-dependencies */
import * as Sentry from '@sentry/angular';
import { CaptureContext } from '@sentry/types';
import type { ErrorEvent, EventHint } from '@sentry/types';

import { IEnvironmentConfig } from '@sondermind/configuration';
import { ApiError, API_ERROR_NAME } from '@sondermind/utilities/models-http';

/**
 * A benign client abort is an ApiError that failed because the client had no
 * network connectivity (`status === 0` AND `offline === true`). These are
 * non-actionable client-side conditions, so they are dropped before reaching
 * Sentry.
 *
 * `status === 0` alone is NOT sufficient: Angular also reports 0 for CORS
 * misconfig, DNS failures, and dropped TCP connections that occur while the
 * client is online — those are genuine defects and must reach Sentry. The
 * `offline` flag (stamped by HttpErrorService from `navigator.onLine`)
 * distinguishes the benign case. See {@link ApiError.offline}.
 *
 * Introduced under MER-4772 after MER-4699 made ApiError a real Error subclass,
 * which began surfacing these previously-invisible failures as escalating issues.
 * Genuine 4xx/5xx ApiErrors (status > 0) are intentionally NOT filtered here.
 *
 * Uses duck-type fallback (`name === 'ApiError'`) to handle cases where
 * `instanceof` fails across lazy-loaded bundles with duplicated module identity.
 */
function isBenignClientAbort(exception: unknown): boolean {
  if (exception instanceof ApiError) {
    return exception.status === 0 && exception.offline === true;
  }
  // Duck-type fallback for cross-bundle identity mismatches. Requires `status` to be
  // a number to avoid false positives from unrelated errors that happen to set .name.
  if (exception instanceof Error && exception.name === API_ERROR_NAME) {
    const { status, offline } = exception as { status?: unknown; offline?: unknown };
    return typeof status === 'number' && status === 0 && offline === true;
  }
  return false;
}

/** Tracks suppressed benign-abort count. Test-only — not surfaced to any production sink. */
export const _testBenignAbortDropCount = { count: 0 };

/**
 * Builds the `beforeSend` hook passed to `Sentry.init`. Exported so the
 * filtering logic can be unit-tested directly (Sentry.init's options are not
 * reachable through the non-writable ESM export at runtime).
 */
export function buildBeforeSend(
  config: Pick<IEnvironmentConfig, 'sentryDebug'>
): (event: ErrorEvent, hint?: EventHint) => ErrorEvent | null {
  return (event, hint) => {
    if (isBenignClientAbort(hint?.originalException)) {
      _testBenignAbortDropCount.count += 1;
      return null;
    }

    if (config.sentryDebug) {
      // console.error is intentional here: this hook runs at Sentry.init time, before
      // Angular's DI graph is ready, so Logger cannot be injected.
      // eslint-disable-next-line no-console
      console.error('Sentry Error (debug): ', event.event_id);
      return event;
    }

    return event;
  };
}

export class SentryClient {
  static captureException(exception: any, captureContext?: CaptureContext): string {
    return Sentry.captureException(exception, captureContext);
  }

  static captureMessage(message: string, captureContext?: CaptureContext | Sentry.SeverityLevel): string {
    return Sentry.captureMessage(message, captureContext);
  }

  static configureScope(callback: (scope: Sentry.Scope) => void): void {
    const scope = Sentry.getCurrentScope();
    if (scope) {
      callback(scope);
    }
  }

  static initialize(config: IEnvironmentConfig, commitHash: string): void {
    Sentry.init({
      ...config.sentryDSN,
      release: commitHash,
      ignoreErrors: [
        'Non-Error exception captured',
        'Handled unknown error'
      ],
      beforeSend: buildBeforeSend(config)
    });
  }

  static setUser(user: Sentry.User): void {
    Sentry.setUser(user);
  }
}
